import { useState, useMemo, useRef, useEffect, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Eye, EyeSlash, ArrowRight, ArrowLeft, PencilSimple, CircleNotch } from '@phosphor-icons/react'
import { authHelpers } from '../lib/supabase'
import { captureSignupIntent } from '../lib/signupIntent'
import PageTransition from '../components/PageTransition'
import Navbar from '../components/Navbar'
import ShimmerButton from '../components/magicui/ShimmerButton'
import BrandLogo from '../components/website/BrandLogo'

/**
 * Vercel / Linear two-step sign-up flow (2026-04-18).
 *
 * Step 1 — Email:
 *   - Single email input, autofocus.
 *   - "Continuer" advances to step 2 (same UX as Login).
 *   - Link to /login for existing users.
 *
 * Step 2 — Profile + password:
 *   - Email pill with "Modifier" (back to step 1, preserves value).
 *   - First name.
 *   - Phone (international format hint).
 *   - Password + live strength meter + criteria checklist.
 *   - Confirm password with match/mismatch validation.
 *   - Terms + privacy checkbox (required).
 *   - "Créer mon compte" submits.
 *
 * Progress overlay (signupStep 1→3) kicks in during submission — same
 * animation Mustapha validated earlier. Hard-refresh to /check-email
 * on success so AuthContext fully rehydrates.
 *
 * Zero gradient CTAs, zero card-in-card chrome. Single-plane form.
 */
export default function Signup() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<'email' | 'profile'>('email')
  const [direction, setDirection] = useState<1 | -1>(1)
  const [firstName, setFirstName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [loading, setLoading] = useState(false)
  const [signupStep, setSignupStep] = useState(0)
  const firstNameInputRef = useRef<HTMLInputElement | null>(null)

  // Password strength
  const passwordCriteria = useMemo(() => ({
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
  }), [password])

  const strengthScore = useMemo(() =>
    Object.values(passwordCriteria).filter(Boolean).length,
    [passwordCriteria]
  )

  const strengthLabel = [
    '',
    t('signup.strength.weak'),
    t('signup.strength.medium'),
    t('signup.strength.almost'),
    t('signup.strength.strong'),
  ] as const
  // Strength bar fill: index 0 = empty (neutral token), 1-4 = traffic-light
  // semantic progression. Saturated hex retained on 1-4 because they're the
  // shared industry reference across password UIs (red, amber, violet,
  // green at the 500 saturation). Empty state uses a cross-mode token.
  const strengthColor = ['var(--bn-border-strong)', '#EF4444', '#F59E0B', '#8B5CF6', '#22C55E']

  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword
  const passwordsDontMatch = confirmPassword.length > 0 && password !== confirmPassword
  const isPasswordStrong = strengthScore === 4
  const canSubmit = Boolean(firstName.trim()) && phone.length > 0 && isPasswordStrong && passwordsMatch

  const phoneRegex = /^\+?[0-9]{8,15}$/

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

  useEffect(() => {
    if (step === 'profile' && firstNameInputRef.current && !firstName) {
      firstNameInputRef.current.focus()
    }
  }, [step, firstName])

  const handleEmailNext = (e: FormEvent) => {
    e.preventDefault()
    setEmailError('')
    if (!isValidEmail(email)) {
      setEmailError(t('signup.email_invalid'))
      return
    }
    // Fire-and-forget lead capture. Never blocks the step transition:
    // if the edge fn is down or the user drops off before completion,
    // the reminder cron picks them up 24h later.
    captureSignupIntent({ email, source: 'signup' })
    setDirection(1)
    setStep('profile')
  }

  const handleBackToEmail = () => {
    setDirection(-1)
    setError('')
    setStep('email')
  }

  const handleSwitchToLogin = () => {
    const trimmed = email.trim()
    const target = trimmed.length > 0 && isValidEmail(trimmed)
      ? `/login?email=${encodeURIComponent(trimmed)}`
      : '/login'
    // Soft client-side nav — App-level AnimatePresence cross-fades.
    navigate(target)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (!isPasswordStrong) {
      setError(t('signup.validation.password_weak'))
      setLoading(false)
      return
    }

    if (password !== confirmPassword) {
      setError(t('signup.validation.passwords_mismatch'))
      setLoading(false)
      return
    }

    if (!firstName.trim()) {
      setError(t('signup.validation.first_name_required'))
      setLoading(false)
      return
    }

    // eslint-disable-next-line no-useless-escape
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '')
    if (!cleanPhone || !phoneRegex.test(cleanPhone)) {
      setError(t('signup.validation.phone_invalid'))
      setLoading(false)
      return
    }

    try {
      setSignupStep(1)
      const signupPromise = authHelpers.signUp(email, password, firstName, cleanPhone)
      setTimeout(() => setSignupStep(2), 1200)
      setTimeout(() => setSignupStep(3), 2400)

      setTimeout(async () => {
        const { error: signupError } = await signupPromise
        if (signupError) {
          setSignupStep(0)
          setLoading(false)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const code = (signupError as any)?.code
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const status = (signupError as any)?.status
          const msg = signupError.message || ''

          let toastMsg = t('signup.errors.signup_failed')
          if (code === 'weak_password' || msg.includes('known to be weak')) {
            toastMsg = t('signup.errors.weak_password')
          } else if (msg.includes('already registered') || code === 'user_already_exists') {
            toastMsg = t('signup.errors.already_registered')
          } else if (status === 429 || code === 'over_email_send_rate_limit' || msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
            toastMsg = t('signup.errors.rate_limit')
          }

          toast.error(toastMsg, { duration: 5000 })
          return
        }
        setSignupStep(0)
        setLoading(false)
        // Mark the intent completed so the reminder cron stops targeting them.
        captureSignupIntent({ email, source: 'signup', completed: true })
        window.location.href = `/check-email?email=${encodeURIComponent(email)}`
      }, 3500)

    } catch (_err) {
      setSignupStep(0)
      setLoading(false)
      setError(t('signup.errors.generic'))
      toast.error(t('signup.errors.toast_generic'))
    }
  }

  const stepVariants = {
    enter: (dir: 1 | -1) => ({ opacity: 0, x: dir * 40 }),
    center: { opacity: 1, x: 0 },
    exit: (dir: 1 | -1) => ({ opacity: 0, x: dir * -40 }),
  }

  return (
    <>
      {/* Creation progress overlay — displayed during signup. Full-screen
          dark backdrop cross-mode (submission feedback, keeps user focus). */}
      {signupStep > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 backdrop-blur-xl"
        >
          <CircleNotch className="animate-spin h-12 w-12 text-[var(--bn-accent)] mb-6" />

          <motion.p
            key={signupStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-white text-lg font-light mb-4"
          >
            {signupStep === 1 && t('signup.progress.step_1')}
            {signupStep === 2 && t('signup.progress.step_2')}
            {signupStep === 3 && t('signup.progress.step_3')}
          </motion.p>

          <div className="w-64 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[var(--bn-accent)] rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: signupStep === 1 ? '33%' : signupStep === 2 ? '66%' : '95%' }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>

          <p className="text-white/60 text-sm font-light mt-4">
            {t('signup.progress.hint')}
          </p>
        </motion.div>
      )}

      <PageTransition>
        <div className="min-h-[100dvh] bg-[var(--bn-bg-page)] flex flex-col relative transition-colors duration-300">
          <Navbar variant="auth" />

          <main className="relative z-10 flex-1 flex items-start justify-center px-4 sm:px-6 lg:px-8 pt-6 pb-12 sm:items-center sm:pt-8">
            <div className="w-full max-w-[420px]">

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="text-center mb-10 sm:mb-12"
              >
                <h1 className="text-2xl sm:text-3xl font-light tracking-tight text-[var(--bn-text-primary)] mb-2">
                  {t('signup.heading_prefix')}{' '}
                  <BrandLogo variant="hero" className="inline-flex align-baseline text-[var(--bn-text-primary)]" />
                </h1>
                <p className="text-sm text-[var(--bn-text-subtle)] font-light">
                  {step === 'email' ? t('signup.step_email_label') : t('signup.step_profile_label')}
                </p>
              </motion.div>

              <AnimatePresence mode="wait" custom={direction}>
                {step === 'email' && (
                  <motion.form
                    key="step-email"
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    onSubmit={handleEmailNext}
                    className="space-y-5"
                    autoComplete="on"
                  >
                    <div>
                      <label htmlFor="email" className="sr-only">
                        {t('signup.email_label')}
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        autoFocus
                        required
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value)
                          if (emailError) setEmailError('')
                        }}
                        aria-invalid={Boolean(emailError) || undefined}
                        aria-describedby={emailError ? 'email-error' : undefined}
                        placeholder={t('signup.email_placeholder')}
                        className={`w-full bg-[var(--bn-bg-surface)] border ${
                          emailError
                            ? 'border-[var(--bn-state-error-border)] focus:border-[var(--bn-state-error-text)]'
                            : 'border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)]'
                        } rounded-[var(--bn-radius-action)] px-4 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]`}
                      />
                      {emailError && (
                        <p id="email-error" className="mt-2 text-xs text-[var(--bn-state-error-text)] font-light">
                          {emailError}
                        </p>
                      )}
                    </div>

                    <ShimmerButton
                      type="submit"
                      className="w-full py-3.5 text-base font-medium shadow-[var(--bn-accent-glow)] hover:shadow-[var(--bn-accent-glow-strong)]"
                    >
                      {t('signup.continue_cta')}
                      <ArrowRight className="h-4 w-4" weight="bold" />
                    </ShimmerButton>

                    <p className="pt-1 text-center text-[11px] leading-relaxed text-[var(--bn-text-subtle)] font-light">
                      {t('signup.continue_accepts')}{' '}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 decoration-[var(--bn-border-strong)] hover:text-[var(--bn-text-body)] transition-colors"
                      >
                        {t('signup.terms_link')}
                      </a>{' '}
                      {t('signup.terms_and')}{' '}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 decoration-[var(--bn-border-strong)] hover:text-[var(--bn-text-body)] transition-colors"
                      >
                        {t('signup.privacy_link')}
                      </a>
                      .
                    </p>

                    <div className="pt-4 text-center text-sm font-light text-[var(--bn-text-subtle)]">
                      {t('signup.divider_existing_user')}{' '}
                      <button
                        type="button"
                        onClick={handleSwitchToLogin}
                        className="text-[var(--bn-accent)] hover:text-[var(--bn-accent-deep)] font-medium transition-colors"
                      >
                        {t('signup.login_cta')}
                      </button>
                    </div>
                  </motion.form>
                )}

                {step === 'profile' && (
                  <motion.form
                    key="step-profile"
                    custom={direction}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    autoComplete="on"
                  >
                    {/* Email pill — preserved across step transitions. */}
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className="w-full group flex items-center justify-between gap-3 bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] rounded-[var(--bn-radius-action)] px-4 py-3 text-left transition-colors"
                      aria-label={t('signup.change_email')}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <ArrowLeft className="h-3.5 w-3.5 shrink-0 text-[var(--bn-text-subtle)] group-hover:text-[var(--bn-text-body)] transition-colors" weight="bold" />
                        <span className="truncate text-sm text-[var(--bn-text-primary)] font-light">
                          {email}
                        </span>
                      </span>
                      <span className="flex items-center gap-1 shrink-0 text-xs text-[var(--bn-accent)] opacity-80 group-hover:opacity-100 transition-opacity">
                        <PencilSimple className="h-3 w-3" weight="bold" />
                        {t('signup.change_email')}
                      </span>
                    </button>

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-[var(--bn-radius-action)] border border-[var(--bn-state-error-border)] bg-[var(--bn-state-error-bg)] px-4 py-3"
                      >
                        <p className="text-xs font-light text-[var(--bn-state-error-text)]">{error}</p>
                      </motion.div>
                    )}

                    <div>
                      <label htmlFor="firstName" className="block text-[11px] font-medium text-[var(--bn-text-subtle)] uppercase tracking-[0.12em] mb-2">
                        {t('signup.first_name_label')}
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        autoComplete="given-name"
                        required
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        ref={firstNameInputRef}
                        placeholder={t('signup.first_name_placeholder')}
                        className="w-full bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)] rounded-[var(--bn-radius-action)] px-4 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-[11px] font-medium text-[var(--bn-text-subtle)] uppercase tracking-[0.12em] mb-2">
                        {t('signup.phone_label')}
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        autoComplete="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={t('signup.phone_placeholder')}
                        className="w-full bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)] rounded-[var(--bn-radius-action)] px-4 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]"
                      />
                      <p className="mt-1.5 text-[11px] text-[var(--bn-text-subtle)] font-light">
                        {t('signup.phone_hint')}
                      </p>
                    </div>

                    <div>
                      <label htmlFor="password" className="block text-[11px] font-medium text-[var(--bn-text-subtle)] uppercase tracking-[0.12em] mb-2">
                        {t('signup.password_label')}
                      </label>
                      <div className="relative">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••••"
                          className="w-full bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)] rounded-[var(--bn-radius-action)] px-4 pr-12 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-[var(--bn-text-subtle)] hover:text-[var(--bn-text-body)] hover:bg-[var(--bn-bg-surface-hover)] transition-colors"
                        >
                          {showPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>

                      {password.length > 0 && (
                        <div className="mt-3 space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 flex gap-1">
                              {[1, 2, 3, 4].map((i) => (
                                <div
                                  key={i}
                                  className="h-1.5 flex-1 rounded-full transition-all duration-500"
                                  style={{
                                    backgroundColor: i <= strengthScore ? strengthColor[strengthScore] : '#333',
                                  }}
                                />
                              ))}
                            </div>
                            <motion.span
                              key={strengthScore}
                              initial={{ opacity: 0, x: 5 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="text-xs font-medium whitespace-nowrap"
                              style={{ color: strengthColor[strengthScore] }}
                            >
                              {strengthLabel[strengthScore]}
                            </motion.span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                            {[
                              { key: 'length' as const, label: t('signup.criteria.length') },
                              { key: 'uppercase' as const, label: t('signup.criteria.uppercase') },
                              { key: 'number' as const, label: t('signup.criteria.number') },
                              { key: 'special' as const, label: t('signup.criteria.special') },
                            ].map(({ key, label }) => (
                              <div key={key} className="flex items-center gap-1.5">
                                <motion.span
                                  animate={passwordCriteria[key] ? { scale: [1, 1.2, 1] } : {}}
                                  transition={{ duration: 0.2 }}
                                  className="text-xs"
                                  style={{ color: passwordCriteria[key] ? 'var(--bn-state-success-icon)' : 'var(--bn-text-faint)' }}
                                >
                                  {passwordCriteria[key] ? '✓' : '○'}
                                </motion.span>
                                <span
                                  className="text-xs font-light transition-colors duration-300"
                                  style={{ color: passwordCriteria[key] ? 'var(--bn-text-primary)' : 'var(--bn-text-faint)' }}
                                >
                                  {label}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-[11px] font-medium text-[var(--bn-text-subtle)] uppercase tracking-[0.12em] mb-2">
                        {t('signup.confirm_password_label')}
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          required
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="••••••••••"
                          className="w-full bg-[var(--bn-bg-surface)] border border-[var(--bn-border)] hover:border-[var(--bn-border-strong)] focus:border-[var(--bn-accent-border)] rounded-[var(--bn-radius-action)] px-4 pr-12 py-3.5 text-base text-[var(--bn-text-primary)] placeholder-[var(--bn-text-muted)] font-light transition-colors duration-150 focus:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-lg text-[var(--bn-text-subtle)] hover:text-[var(--bn-text-body)] hover:bg-[var(--bn-bg-surface-hover)] transition-colors"
                        >
                          {showConfirmPassword ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {passwordsMatch && (
                        <motion.p
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs font-light text-[var(--bn-state-success-icon)]"
                        >
                          {t('signup.passwords_match')}
                        </motion.p>
                      )}
                      {passwordsDontMatch && (
                        <motion.p
                          initial={{ opacity: 0, y: -4 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs font-light text-[var(--bn-state-error-text)]"
                        >
                          {t('signup.passwords_mismatch')}
                        </motion.p>
                      )}
                    </div>

                    <div className="flex items-start gap-2.5 pt-2">
                      <input
                        id="terms"
                        name="terms"
                        type="checkbox"
                        required
                        className="mt-0.5 h-4 w-4 rounded border-[var(--bn-border-strong)] bg-[var(--bn-bg-surface-weak)] text-[var(--bn-accent)] focus:ring-[var(--bn-accent)] focus:ring-2 cursor-pointer"
                      />
                      <label htmlFor="terms" className="text-xs text-[var(--bn-text-subtle)] font-light cursor-pointer leading-relaxed">
                        {t('signup.terms_prefix')}{' '}
                        <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[var(--bn-accent)] hover:text-[var(--bn-accent-deep)] transition-colors">
                          {t('signup.terms_link')}
                        </a>{' '}
                        {t('signup.terms_and')}{' '}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[var(--bn-accent)] hover:text-[var(--bn-accent-deep)] transition-colors">
                          {t('signup.privacy_link')}
                        </a>
                      </label>
                    </div>

                    <ShimmerButton
                      type="submit"
                      disabled={loading || !canSubmit}
                      className="w-full py-3.5 text-base font-medium mt-2 shadow-[var(--bn-accent-glow)] hover:shadow-[var(--bn-accent-glow-strong)]"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <CircleNotch className="w-4 h-4 animate-spin" />
                          {t('signup.submit_loading')}
                        </span>
                      ) : (
                        <>
                          {t('signup.submit_cta')}
                          <ArrowRight className="h-4 w-4" weight="bold" />
                        </>
                      )}
                    </ShimmerButton>
                  </motion.form>
                )}
              </AnimatePresence>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mt-10 sm:mt-12 text-center"
              >
                <Link
                  to="/"
                  className="text-xs text-[var(--bn-text-subtle)] hover:text-[var(--bn-text-body)] transition-colors font-light"
                >
                  {t('signup.back_home')}
                </Link>
              </motion.div>
            </div>
          </main>
        </div>
      </PageTransition>
    </>
  )
}
